import React from 'react';
import useDoctorScheduleStore from './useDoctorScheduleStore';
import ScheduleBadge from './ScheduleBadge';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_SHORT = { monday: 'MON', tuesday: 'TUE', wednesday: 'WED', thursday: 'THU', friday: 'FRI', saturday: 'SAT', sunday: 'SUN' };

function DoctorCard({ doctor, translations }) {
  const isSync = doctor.source_label === 'synced';
  const badgeClass = isSync
    ? 'doctor-schedule-mgmt__source-badge--synced'
    : 'doctor-schedule-mgmt__source-badge--manual';
  const badgeText = isSync ? translations.badges.synced : translations.badges.manual;

  return (
    <div className="doctor-schedule-mgmt__doctor-card card">
      <div className="card-body">
        <div className="doctor-schedule-mgmt__card-top">
          <a href={doctor.profile_path} className="doctor-schedule-mgmt__card-link">
            <div className="doctor-schedule-mgmt__doctor-avatar mr-2">
              {doctor.avatar_url ? (
                <img src={doctor.avatar_url} alt={doctor.name} className="doctor-schedule-mgmt__avatar-img" />
              ) : (
                <div className="doctor-schedule-mgmt__avatar-placeholder">
                  <i className="zmdi zmdi-account" />
                </div>
              )}
            </div>
            <div>
              <div className="doctor-schedule-mgmt__doctor-name-row">
                <strong className="doctor-schedule-mgmt__card-name" title={doctor.name}>
                  {doctor.name}
                </strong>
                <span className={`doctor-schedule-mgmt__source-badge ml-2 ${badgeClass}`}>
                  {badgeText}
                </span>
              </div>
              <div className="doctor-schedule-mgmt__doctor-specialty">
                {doctor.speciality}
              </div>
            </div>
          </a>
          <a href={doctor.profile_path} className="doctor-schedule-mgmt__card-eye-link" aria-label={doctor.name}>
            <i className="zmdi zmdi-eye doctor-schedule-mgmt__card-eye" />
          </a>
        </div>

        <div className="doctor-schedule-mgmt__mini-strip">
          <div className="doctor-schedule-mgmt__mini-header">
            {DAYS.map((day) => (
              <div key={day} className="doctor-schedule-mgmt__mini-day-label">
                {DAY_SHORT[day]}
              </div>
            ))}
          </div>
          <div className="doctor-schedule-mgmt__mini-slots">
            {DAYS.map((day) => {
              const slots = doctor.schedules[day] || [];
              return (
                <div key={day} className="doctor-schedule-mgmt__mini-day-col">
                  {slots.length === 0 ? (
                    <span className="doctor-schedule-mgmt__mini-empty" />
                  ) : (
                    slots.map((slot, i) => (
                      <ScheduleBadge
                        key={`${slot.from_time}-${i}`}
                        fromTime={slot.from_time}
                        toTime={slot.to_time}
                        type={slot.type}
                        label={slot.label}
                        source={slot.source}
                        size="mini"
                        scheduleLabels={translations.schedule_types}
                      />
                    ))
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="doctor-schedule-mgmt__card-footer">
          <div className="doctor-schedule-mgmt__doctor-branch text-muted">
            <i className="zmdi zmdi-hospital mr-1" />
            {doctor.hospital_branch}
          </div>
          <span className="doctor-schedule-mgmt__slots-count">
            {doctor.total_slots_per_week} {translations.badges.slots_per_week}
          </span>
        </div>
      </div>
    </div>
  );
}

function DoctorCardView({ translations }) {
  const doctors = useDoctorScheduleStore((s) => s.doctors);
  const loading = useDoctorScheduleStore((s) => s.loading);

  if (loading) {
    const skeletonCards = Array.from({ length: 6 });
    return (
      <div className="row doctor-schedule-mgmt__card-grid">
        {skeletonCards.map((_, i) => (
          <div key={i} className="col-12 col-md-6 col-xl-4 mb-3">
            <div className="doctor-schedule-mgmt__doctor-card card">
              <div className="card-body">
                <div className="d-flex align-items-center mb-3">
                  <div className="doctor-schedule-mgmt__skeleton doctor-schedule-mgmt__skeleton--circle mr-2" />
                  <div className="flex-grow-1">
                    <div className="doctor-schedule-mgmt__skeleton doctor-schedule-mgmt__skeleton--text-lg mb-1" />
                    <div className="doctor-schedule-mgmt__skeleton doctor-schedule-mgmt__skeleton--text-sm" />
                  </div>
                </div>
                <div className="doctor-schedule-mgmt__skeleton doctor-schedule-mgmt__skeleton--strip mb-2" />
                <div className="d-flex justify-content-between">
                  <div className="doctor-schedule-mgmt__skeleton doctor-schedule-mgmt__skeleton--text-sm" style={{ width: '40%' }} />
                  <div className="doctor-schedule-mgmt__skeleton doctor-schedule-mgmt__skeleton--text-xs" style={{ width: '20%' }} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (doctors.length === 0) {
    return (
      <div className="text-center py-5 text-muted">
        <i className="zmdi zmdi-calendar-note zmdi-hc-3x mb-3 d-block" />
        {translations.empty_state.no_results}
      </div>
    );
  }

  return (
    <div className="row doctor-schedule-mgmt__card-grid">
      {doctors.map((doctor) => (
        <div key={doctor.id} className="col-12 col-md-6 col-xl-4 mb-3">
          <DoctorCard doctor={doctor} translations={translations} />
        </div>
      ))}
    </div>
  );
}

export default DoctorCardView;
